import React, { useEffect, useMemo, useState } from "react";
import {
	Accordion,
	Button,
	Card,
	Col,
	Container,
	Form,
	Offcanvas,
	Row,
	Stack,
} from "react-bootstrap";
import { FaCompress, FaExpand } from "react-icons/fa";

import AdminLayout from "./AdminLayout";
import BlogEditor from "./components/blog/BlogEditor";
import BlogSeoPanel from "./components/blog/BlogSeoPanel";
import BlogTable from "./components/blog/BlogTable";
import {
	BlogRecord,
	BlogSeo,
	BlogStatus,
	archiveBlog,
	createAdminBlog,
	getAdminBlog,
	getAdminBlogs,
	restoreBlog,
	updateAdminBlog,
	uploadBlogImage,
} from "../../services/blogService";

type ArchivedFilter = "active" | "all" | "archived";
type StatusFilter = "all" | BlogStatus;

interface BlogFormState {
	title: string;
	slug: string;
	excerpt: string;
	contentHtml: string;
	contentJson: unknown;
	status: BlogStatus;
	coverImage: { url: string; key: string; focalX: number; focalY: number };
	seo: BlogSeo;
	publishedAt: string | null;
	archivedAt: string | null;
}

const createEmptyForm = (): BlogFormState => ({
	title: "",
	slug: "",
	excerpt: "",
	contentHtml: "",
	contentJson: null,
	status: "draft",
	coverImage: { url: "", key: "", focalX: 50, focalY: 50 },
	seo: {
		metaTitle: "",
		metaDescription: "",
		canonicalUrl: "",
		ogImageUrl: "",
		robotsIndex: true,
		robotsFollow: true,
	},
	publishedAt: null,
	archivedAt: null,
});

const slugFromTitle = (value: string) =>
	value
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

const hasPublishableContent = (html: string) => {
	const textOnly = html.replace(/<[^>]+>/g, "").trim();
	return textOnly.length > 0 || /<img\b/i.test(html);
};

const isValidUrl = (value: string) => {
	if (!value.trim()) {
		return true;
	}
	try {
		const url = new URL(value);
		return url.protocol === "http:" || url.protocol === "https:";
	} catch (error) {
		return false;
	}
};

const mapBlogToForm = (blog: BlogRecord): BlogFormState => ({
	title: blog.title || "",
	slug: blog.slug || "",
	excerpt: blog.excerpt || "",
	contentHtml: blog.contentHtml || "",
	contentJson: blog.contentJson ?? null,
	status: blog.status || "draft",
	coverImage: {
		url: blog.coverImage?.url || "",
		key: blog.coverImage?.key || "",
		focalX:
			typeof blog.coverImage?.focalX === "number" ? blog.coverImage.focalX : 50,
		focalY:
			typeof blog.coverImage?.focalY === "number" ? blog.coverImage.focalY : 50,
	},
	seo: {
		metaTitle: blog.seo?.metaTitle || "",
		metaDescription: blog.seo?.metaDescription || "",
		canonicalUrl: blog.seo?.canonicalUrl || "",
		ogImageUrl: blog.seo?.ogImageUrl || "",
		robotsIndex:
			typeof blog.seo?.robotsIndex === "boolean" ? blog.seo.robotsIndex : true,
		robotsFollow:
			typeof blog.seo?.robotsFollow === "boolean" ? blog.seo.robotsFollow : true,
	},
	publishedAt: blog.publishedAt,
	archivedAt: blog.archivedAt,
});

const toPayload = (form: BlogFormState, status: BlogStatus) => ({
	title: form.title.trim(),
	slug: form.slug.trim(),
	excerpt: form.excerpt.trim(),
	contentHtml: form.contentHtml,
	contentJson: form.contentJson,
	status,
	coverImage: form.coverImage,
	seo: form.seo,
});

const clampPercent = (value: number) => {
	const parsed = Number(value);
	if (!Number.isFinite(parsed)) {
		return 50;
	}
	return Math.min(100, Math.max(0, Math.round(parsed * 100) / 100));
};

const AdminBlogsPage = () => {
	const [blogs, setBlogs] = useState<BlogRecord[]>([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
	const [archivedFilter, setArchivedFilter] =
		useState<ArchivedFilter>("active");
	const [showDrawer, setShowDrawer] = useState(false);
	const [drawerFullscreen, setDrawerFullscreen] = useState(false);
	const [selectedBlogId, setSelectedBlogId] = useState<string | null>(null);
	const [formState, setFormState] = useState<BlogFormState>(createEmptyForm());
	const [imageMaxHeight, setImageMaxHeight] = useState(700);
	const [slugTouched, setSlugTouched] = useState(false);
	const [editorResetToken, setEditorResetToken] = useState("new");
	const [initialSnapshot, setInitialSnapshot] = useState("");

	const fetchBlogs = async () => {
		try {
			setLoading(true);
			const response = await getAdminBlogs({
				q: searchQuery || undefined,
				status: statusFilter === "all" ? undefined : statusFilter,
				archived: archivedFilter,
				page: 1,
				limit: 100,
			});
			setBlogs(response.data.items || []);
		} catch (error) {
			console.error("Failed to fetch blogs:", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		void fetchBlogs();
	}, [searchQuery, statusFilter, archivedFilter]);

	const currentSnapshot = useMemo(
		() => JSON.stringify(toPayload(formState, formState.status)),
		[formState],
	);
	const isDirty = showDrawer && initialSnapshot !== currentSnapshot;

	const openCreateDrawer = () => {
		const nextForm = createEmptyForm();
		setSelectedBlogId(null);
		setFormState(nextForm);
		setSlugTouched(false);
		setEditorResetToken(`new-${Date.now()}`);
		setInitialSnapshot(JSON.stringify(toPayload(nextForm, nextForm.status)));
		setShowDrawer(true);
	};

	const openEditDrawer = async (blogId: string) => {
		try {
			const response = await getAdminBlog(blogId);
			const mapped = mapBlogToForm(response.data);
			setSelectedBlogId(blogId);
			setFormState(mapped);
			setSlugTouched(true);
			setEditorResetToken(`${blogId}-${Date.now()}`);
			setInitialSnapshot(JSON.stringify(toPayload(mapped, mapped.status)));
			setShowDrawer(true);
		} catch (error) {
			console.error("Failed to load blog:", error);
			alert("Failed to load blog.");
		}
	};

	const closeDrawer = () => {
		if (isDirty && !window.confirm("Discard unsaved changes?")) {
			return;
		}
		setShowDrawer(false);
		setDrawerFullscreen(false);
	};

	const validateForm = (status: BlogStatus) => {
		const errors: string[] = [];
		const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

		if (!formState.title.trim()) errors.push("Title is required.");
		if (formState.title.trim().length > 140)
			errors.push("Title must be 140 characters or fewer.");
		if (!formState.slug.trim()) errors.push("Slug is required.");
		if (!slugRegex.test(formState.slug.trim()))
			errors.push(
				"Slug must contain lowercase letters/numbers separated by hyphens.",
			);
		if (formState.excerpt.trim().length > 300)
			errors.push("Excerpt must be 300 characters or fewer.");
		if (formState.seo.metaTitle.trim().length > 60)
			errors.push("SEO title must be 60 characters or fewer.");
		if (formState.seo.metaDescription.trim().length > 160)
			errors.push("SEO description must be 160 characters or fewer.");
		if (!isValidUrl(formState.seo.canonicalUrl))
			errors.push("Canonical URL must be a valid HTTP/HTTPS URL.");
		if (!isValidUrl(formState.seo.ogImageUrl))
			errors.push("OG image URL must be a valid HTTP/HTTPS URL.");
		if (status === "published" && !hasPublishableContent(formState.contentHtml)) {
			errors.push("Published blogs require content.");
		}

		return errors;
	};

	const saveBlog = async (targetStatus: BlogStatus) => {
		const errors = validateForm(targetStatus);
		if (errors.length) {
			alert(errors.join("\n"));
			return;
		}

		try {
			setSaving(true);
			const payload = toPayload(formState, targetStatus);

			let response;
			if (selectedBlogId) {
				response = await updateAdminBlog(selectedBlogId, payload);
			} else {
				response = await createAdminBlog(payload);
				setSelectedBlogId(response.data._id);
			}

			const mapped = mapBlogToForm(response.data);
			setFormState(mapped);
			setInitialSnapshot(JSON.stringify(toPayload(mapped, mapped.status)));
			setEditorResetToken(`${response.data._id}-${Date.now()}`);
			await fetchBlogs();
		} catch (error: any) {
			console.error("Failed to save blog:", error);
			const message =
				error?.response?.data || "Failed to save blog. Please try again.";
			alert(message);
		} finally {
			setSaving(false);
		}
	};

	const handleArchive = async () => {
		if (!selectedBlogId) return;
		if (!window.confirm("Archive this blog?")) return;
		try {
			const response = await archiveBlog(selectedBlogId);
			const mapped = mapBlogToForm(response.data);
			setFormState(mapped);
			setInitialSnapshot(JSON.stringify(toPayload(mapped, mapped.status)));
			await fetchBlogs();
		} catch (error) {
			console.error("Failed to archive blog:", error);
			alert("Failed to archive blog.");
		}
	};

	const handleRestore = async () => {
		if (!selectedBlogId) return;
		try {
			const response = await restoreBlog(selectedBlogId);
			const mapped = mapBlogToForm(response.data);
			setFormState(mapped);
			setInitialSnapshot(JSON.stringify(toPayload(mapped, mapped.status)));
			await fetchBlogs();
		} catch (error) {
			console.error("Failed to restore blog:", error);
			alert("Failed to restore blog.");
		}
	};

	const handleCoverImageUpload = async (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const file = event.target.files?.[0];
		if (!file) return;
		try {
			const response = await uploadBlogImage(file);
			setFormState((prev) => ({
				...prev,
				coverImage: {
					url: response.data.url,
					key: response.data.key,
					focalX: prev.coverImage.focalX,
					focalY: prev.coverImage.focalY,
				},
			}));
		} catch (error) {
			console.error("Failed to upload cover image:", error);
			alert("Failed to upload cover image.");
		} finally {
			event.target.value = "";
		}
	};

	const uploadInlineImage = async (file: File) => {
		const response = await uploadBlogImage(file);
		return response.data.url;
	};

	const setCoverFocalPoint = (focalX: number, focalY: number) => {
		setFormState((prev) => ({
			...prev,
			coverImage: {
				...prev.coverImage,
				focalX: clampPercent(focalX),
				focalY: clampPercent(focalY),
			},
		}));
	};

	const handleCropPreviewClick = (event: React.MouseEvent<HTMLDivElement>) => {
		const rect = event.currentTarget.getBoundingClientRect();
		const x = ((event.clientX - rect.left) / rect.width) * 100;
		const y = ((event.clientY - rect.top) / rect.height) * 100;
		setCoverFocalPoint(x, y);
	};

	const coverObjectPosition = `${clampPercent(formState.coverImage.focalX)}% ${clampPercent(formState.coverImage.focalY)}%`;

	return (
		<AdminLayout>
			<Container className="py-4">
				<Row className="align-items-end g-3 mb-3">
					<Col md={4}>
						<Form.Group>
							<Form.Label>Search</Form.Label>
							<Form.Control
								value={searchQuery}
								onChange={(event) => setSearchQuery(event.target.value)}
								placeholder="Search by title, slug, or excerpt"
							/>
						</Form.Group>
					</Col>
					<Col md={2}>
						<Form.Group>
							<Form.Label>Status</Form.Label>
							<Form.Select
								value={statusFilter}
								onChange={(event) =>
									setStatusFilter(event.target.value as StatusFilter)
								}
							>
								<option value="all">All</option>
								<option value="draft">Draft</option>
								<option value="published">Published</option>
							</Form.Select>
						</Form.Group>
					</Col>
					<Col md={2}>
						<Form.Group>
							<Form.Label>Archived</Form.Label>
							<Form.Select
								value={archivedFilter}
								onChange={(event) =>
									setArchivedFilter(event.target.value as ArchivedFilter)
								}
							>
								<option value="active">Active</option>
								<option value="all">All</option>
								<option value="archived">Archived</option>
							</Form.Select>
						</Form.Group>
					</Col>
					<Col md="auto" className="ms-md-auto">
						<Button onClick={openCreateDrawer}>Create Blog</Button>
					</Col>
				</Row>

				<Card className="shadow border-0">
					<Card.Body>
						<BlogTable blogs={blogs} loading={loading} onOpen={openEditDrawer} />
					</Card.Body>
				</Card>

				<Offcanvas
					show={showDrawer}
					onHide={closeDrawer}
					placement="end"
					className={`admin-offcanvas admin-offcanvas--blog${drawerFullscreen ? " admin-offcanvas--fullscreen" : ""}`}
				>
					<Offcanvas.Header closeButton>
						<Offcanvas.Title>
							{selectedBlogId ? "Edit Blog" : "Create Blog"}
						</Offcanvas.Title>
						<Button
							variant="outline-secondary"
							size="sm"
							className="ms-2"
							onClick={() => setDrawerFullscreen((prev) => !prev)}
						>
							{drawerFullscreen ? <FaCompress /> : <FaExpand />}
						</Button>
					</Offcanvas.Header>
					<Offcanvas.Body>
						<Card className="border-0 shadow-sm mb-3">
							<Card.Header className="fw-semibold">Content</Card.Header>
							<Card.Body>
								<Form.Group className="mb-3" controlId="blogTitle">
									<Form.Label>Title</Form.Label>
									<Form.Control
										value={formState.title}
										maxLength={140}
										onChange={(event) => {
											const title = event.target.value;
											setFormState((prev) => ({
												...prev,
												title,
												slug: slugTouched ? prev.slug : slugFromTitle(title),
											}));
										}}
									/>
								</Form.Group>
								<Form.Group className="mb-3" controlId="blogSlug">
									<Form.Label>Slug</Form.Label>
									<Form.Control
										value={formState.slug}
										onChange={(event) => {
											setSlugTouched(true);
											setFormState((prev) => ({
												...prev,
												slug: slugFromTitle(event.target.value),
											}));
										}}
									/>
								</Form.Group>
								<Form.Group className="mb-3" controlId="blogExcerpt">
									<Form.Label>Excerpt</Form.Label>
									<Form.Control
										as="textarea"
										rows={3}
										value={formState.excerpt}
										maxLength={300}
										onChange={(event) =>
											setFormState((prev) => ({
												...prev,
												excerpt: event.target.value,
											}))
										}
									/>
									<Form.Text>{formState.excerpt.length}/300</Form.Text>
								</Form.Group>
								<Form.Group className="mb-3" controlId="blogCoverImage">
									<Form.Label>Cover Image</Form.Label>
									<Form.Control type="file" accept="image/*" onChange={handleCoverImageUpload} />
								</Form.Group>
								<Row className="g-3 mb-3">
									<Col md={12}>
										<Form.Group controlId="blogImageMaxHeight">
											<Form.Label>Inline Image Max Height (px)</Form.Label>
											<Form.Control
												type="number"
												min={120}
												max={2400}
												value={imageMaxHeight}
												onChange={(event) =>
													setImageMaxHeight(
														Number.parseInt(event.target.value, 10) || 700,
													)
												}
											/>
										</Form.Group>
									</Col>
								</Row>
								{formState.coverImage.url && (
									<>
										<div className="mb-2 small text-muted">
											Cover crop focus: click preview to choose what stays in
											frame.
										</div>
										<Row className="g-3 mb-2">
											<Col md={6}>
												<div
													className="admin-blog-crop-preview admin-blog-crop-preview--featured"
													onClick={handleCropPreviewClick}
												>
													<img
														src={formState.coverImage.url}
														alt="Featured crop preview"
														className="admin-blog-crop-preview-image"
														style={{ objectPosition: coverObjectPosition }}
													/>
													<span
														className="admin-blog-crop-marker"
														style={{
															left: `${formState.coverImage.focalX}%`,
															top: `${formState.coverImage.focalY}%`,
														}}
													/>
												</div>
												<div className="small text-muted mt-1">
													Featured card preview
												</div>
											</Col>
											<Col md={6}>
												<div
													className="admin-blog-crop-preview admin-blog-crop-preview--small"
													onClick={handleCropPreviewClick}
												>
													<img
														src={formState.coverImage.url}
														alt="Small card crop preview"
														className="admin-blog-crop-preview-image"
														style={{ objectPosition: coverObjectPosition }}
													/>
													<span
														className="admin-blog-crop-marker"
														style={{
															left: `${formState.coverImage.focalX}%`,
															top: `${formState.coverImage.focalY}%`,
														}}
													/>
												</div>
												<div className="small text-muted mt-1">
													Small card preview
												</div>
											</Col>
										</Row>
										<Row className="g-3 mb-3">
											<Col md={6}>
												<Form.Group controlId="coverFocalX">
													<Form.Label>
														Horizontal Focus ({Math.round(formState.coverImage.focalX)}
														%)
													</Form.Label>
													<Form.Range
														min={0}
														max={100}
														step={1}
														value={formState.coverImage.focalX}
														onChange={(event) =>
															setCoverFocalPoint(
																Number(event.target.value),
																formState.coverImage.focalY,
															)
														}
													/>
												</Form.Group>
											</Col>
											<Col md={6}>
												<Form.Group controlId="coverFocalY">
													<Form.Label>
														Vertical Focus ({Math.round(formState.coverImage.focalY)}%)
													</Form.Label>
													<Form.Range
														min={0}
														max={100}
														step={1}
														value={formState.coverImage.focalY}
														onChange={(event) =>
															setCoverFocalPoint(
																formState.coverImage.focalX,
																Number(event.target.value),
															)
														}
													/>
												</Form.Group>
											</Col>
										</Row>
									</>
								)}
								<BlogEditor
									initialContentHtml={formState.contentHtml}
									initialContentJson={formState.contentJson}
									resetToken={editorResetToken}
									onUploadImage={uploadInlineImage}
									imageMaxHeight={imageMaxHeight}
									onChange={({ contentHtml, contentJson }) =>
										setFormState((prev) => ({
											...prev,
											contentHtml,
											contentJson,
										}))
									}
								/>
							</Card.Body>
						</Card>

						<Accordion className="mb-3">
							<Accordion.Item eventKey="seo">
								<Accordion.Header>SEO</Accordion.Header>
								<Accordion.Body>
									<BlogSeoPanel
										seo={formState.seo}
										onChange={(nextSeo) =>
											setFormState((prev) => ({
												...prev,
												seo: nextSeo,
											}))
										}
									/>
								</Accordion.Body>
							</Accordion.Item>
						</Accordion>

						<Card className="border-0 shadow-sm mb-3">
							<Card.Header className="fw-semibold">Publish</Card.Header>
							<Card.Body>
								<Form.Group className="mb-2" controlId="blogStatus">
									<Form.Label>Status</Form.Label>
									<Form.Select
										value={formState.status}
										onChange={(event) =>
											setFormState((prev) => ({
												...prev,
												status: event.target.value as BlogStatus,
											}))
										}
									>
										<option value="draft">Draft</option>
										<option value="published">Published</option>
									</Form.Select>
								</Form.Group>
								<div className="small text-muted">
									Published:{" "}
									{formState.publishedAt
										? new Date(formState.publishedAt).toLocaleString()
										: "-"}
								</div>
								<div className="small text-muted">
									Archived:{" "}
									{formState.archivedAt
										? new Date(formState.archivedAt).toLocaleString()
										: "-"}
								</div>
							</Card.Body>
						</Card>

						{selectedBlogId && (
							<Card className="border-0 shadow-sm mb-3">
								<Card.Header className="fw-semibold">Archive</Card.Header>
								<Card.Body>
									<Stack direction="horizontal" gap={2}>
										<Button
											variant="outline-warning"
											onClick={handleArchive}
											disabled={Boolean(formState.archivedAt)}
										>
											Archive
										</Button>
										<Button
											variant="outline-success"
											onClick={handleRestore}
											disabled={!formState.archivedAt}
										>
											Restore
										</Button>
									</Stack>
								</Card.Body>
							</Card>
						)}

						<Stack direction="horizontal" gap={2} className="justify-content-end">
							<Button variant="outline-secondary" onClick={closeDrawer}>
								Cancel
							</Button>
							<Button
								variant="secondary"
								onClick={() => saveBlog("draft")}
								disabled={saving}
							>
								Save Draft
							</Button>
							<Button
								variant="primary"
								onClick={() => saveBlog("published")}
								disabled={saving}
							>
								Publish
							</Button>
						</Stack>
					</Offcanvas.Body>
				</Offcanvas>
			</Container>
		</AdminLayout>
	);
};

export default AdminBlogsPage;
